// Pieces every game's How to play page shares: the section frame, getting set up, your
// character, the night as every phone sees it, the character list, and tips for everyone.
import type { ReactNode } from "react";
import { CharacterList, KINDS } from "../../components/Characters";
import type { ScriptLoad } from "../../hooks/useScript";

export interface GuideSection {
  id: string;
  label: string;
}

export function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="guide-section stack">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

export function SetupSection({ gameName }: { gameName: string }) {
  return (
    <Section id="setup" title="Getting set up">
      <p>
        You need 5 to 15 players, a phone each, and a circle to sit in. Nobody has to run the game:
        the app is the <strong>Storyteller</strong>, the referee who hands out characters, wakes
        people at night and counts the votes.
      </p>
      <ol>
        <li>
          One player taps <strong>Create a new room</strong> and becomes the host.
        </li>
        <li>Everyone else joins with the room code, the QR code or the share link.</li>
        <li>
          The host chooses <strong>{gameName}</strong> under Game.
        </li>
        <li>
          The host puts the names in the order people are sitting, going clockwise. Voting and
          &quot;neighbours&quot; follow this order, so it is worth getting right.
        </li>
        <li>
          The host taps <strong>Start the game</strong>.
        </li>
      </ol>
    </Section>
  );
}

export function CharacterSection() {
  return (
    <Section id="character" title="Your character">
      <ul>
        <li>
          Shield your screen, then press and hold the card to see your character. It hides again
          when you let go. Keep it secret.
        </li>
        <li>
          The card says which team you are on and what your character can do. When you have read it,
          tap <strong>I know my role</strong>. You can look again at any time with{" "}
          <strong>Role</strong> at the top of the screen.
        </li>
      </ul>
      <p>There are four kinds of character:</p>
      <ul className="plain-list">
        {KINDS.map((k) => (
          <li key={k.type}>
            <strong className={k.team}>{k.label}</strong>
            <span className="muted"> · {k.blurb}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

/** The night as every phone sees it, whichever game. `extra` adds the game's own points, and
 * `timeouts` the game's own exceptions to what happens when time runs out. */
export function NightBasics({ extra, timeouts }: { extra?: ReactNode; timeouts?: ReactNode }) {
  return (
    <ul>
      <li>
        Everyone looks at their own phone and stays quiet. When every player gets a turn (see
        below), your phone buzzes when your turn comes. The table can hear it, so turn off{" "}
        <strong>Vibrate</strong> in the menu (☰) if you would rather it didn&apos;t. Otherwise
        phones never buzz or make a sound at night.
      </li>
      <li>
        When the Storyteller needs you, your screen changes. Tap the players you want to choose and
        tap <strong>Confirm</strong>, or read what you have learned and tap{" "}
        <strong>Got it, back to sleep</strong>.
      </li>
      {extra}
      <li>
        Normally every player gets a turn each night, even with nothing to do. Then you are asked
        who you suspect, who you trust, or who you want to hear from. Tap a player, or{" "}
        <strong>Nobody</strong>, and tap <strong>Confirm</strong>. Your answer goes in your Notes,
        only you see it, and it changes nothing in the game. That way nobody can tell who really
        woke up. If time runs out, the question is skipped and nothing is noted.
      </li>
      <li>
        If you don&apos;t make your character&apos;s choice in time, the app makes it for you at
        random. It picks living players where it can, and never picks you to die. The bottom of
        your screen says whether a choice will be made for you.
      </li>
      {timeouts}
      <li>
        If you don&apos;t tap <strong>Got it, back to sleep</strong> in time, you go back to sleep
        anyway. What you learned is still in your Notes.
      </li>
      <li>
        When every player gets a turn, a turn left alone for a while shows up on the host&apos;s
        phone, so the host can tell that player to look at their phone. Everyone wakes anyway, so
        this gives nothing away about their character.
      </li>
      <li>
        Everything you are told is saved in <strong>Notes</strong> at the top of the screen.
      </li>
    </ul>
  );
}

/** Talk, nominate, speeches, vote, night falls: the same in every game. */
export function DaySteps() {
  return (
    <ol>
      <li>
        <strong>Dawn.</strong> The app says who died in the night. It never says what character they
        were.
      </li>
      <li>
        <strong>Talk.</strong> This is most of the game. Share what you learned, ask questions,
        accuse and defend. You may say anything, true or false, and you may go off for a private
        chat.
      </li>
      <li>
        <strong>Nominate.</strong> After some time to talk, nominations open. Tap a player, then{" "}
        <strong>Nominate</strong>, to put them up for execution. Each living player may nominate
        once a day, and each player can be nominated once a day. You may nominate yourself, or a
        dead player: they don&apos;t die again, but it still counts as the day&apos;s execution.
      </li>
      <li>
        <strong>Speeches.</strong> The nominator says why. The nominee answers.
      </li>
      <li>
        <strong>Vote.</strong> The vote goes clockwise around the circle, one phone at a time,
        starting with the player after the nominee. On your turn, raise your hand and tap{" "}
        <strong>Yes</strong>, or tap <strong>No</strong>. No answer counts as No.
      </li>
      <li>
        <strong>Night falls</strong> when more than half of the living players tap{" "}
        <strong>Ready for night</strong>, or when time runs out.
      </li>
    </ol>
  );
}

export function WhoIsExecuted() {
  return (
    <div className="card stack-sm">
      <h3>Who gets executed?</h3>
      <ul>
        <li>
          To go <strong>on the block</strong>, a player needs Yes votes from at least half of the
          living players. With 7 alive, that is 4 votes. The day screen shows the number.
        </li>
        <li>
          A later nominee has to get <strong>more</strong> votes to take their place. A tie means
          nobody is on the block.
        </li>
        <li>
          When the day ends, whoever is on the block is executed. That is one execution a day at
          most, and none if nobody is on the block.
        </li>
      </ul>
    </div>
  );
}

export function EveryoneTips() {
  return (
    <>
      <h3>Everyone</h3>
      <ul>
        <li>Keep your screen to yourself, especially at night.</li>
        <li>
          Your phone buzzes when it needs you: to see your character, at dawn, when you are
          nominated, on your vote turn and when the game ends, and at night when your turn comes if
          every player gets a turn. iPhones can&apos;t buzz from a web page, so turn on{" "}
          <strong>Sound</strong> in the menu (☰) for a chime instead.
        </li>
        <li>Ask questions. Explaining the rules out loud during the day is fine.</li>
        <li>
          If your phone dies, the host can give you a rejoin code from the menu (☰) so you can take
          your seat back on another phone.
        </li>
      </ul>
    </>
  );
}

export function ScriptCharacters({ script }: { script: ScriptLoad }) {
  if (script === "loading") return <p className="small muted">Loading the characters…</p>;
  if (script === "error") {
    return (
      <p className="error-text">
        Could not load the characters. Check your connection and reload the page.
      </p>
    );
  }
  return (
    <>
      <p className="muted">
        This game uses the {script.name} characters. Only some of them are in each game. During a
        game, open the menu (☰) and tap <strong>Characters</strong> to see this list without
        leaving your seat.
      </p>
      <CharacterList roles={script.roles} />
    </>
  );
}
